// src/pages/invoices/components/ItemDropdown.tsx
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Item } from '@/types/items.types';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import * as React from 'react';

interface ItemDropdownProps {
  items: Item[];
  value: string | undefined;
  onChange: (itemId: string, item: Item | null) => void;
  onAddNew: () => void;
  placeholder?: string;
  error?: boolean;
}

const ItemDropdown = ({
  items,
  value,
  onChange,
  onAddNew,
  placeholder = 'Select item...',
  error,
}: ItemDropdownProps) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const selectedItem = items.find((i) => i._id === value);

  const filteredItems = items.filter((item) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      item.itemName.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  const handleSelect = (item: Item) => {
    onChange(item._id, item);
    setOpen(false);
    setSearch('');
  };

  const handleAddNew = () => {
    setOpen(false);
    onAddNew();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        className={cn(
          'flex h-9 w-full min-w-[200px] max-w-96 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
          'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'hover:bg-accent hover:text-accent-foreground',
          error && 'border-destructive focus:ring-destructive'
        )}
      >
        {selectedItem ? (
          <span className="truncate text-left flex-1">{selectedItem.itemName}</span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>

      <PopoverContent
        className="w-[600px] max-w-[280px] md:max-w-lg lg:max-w-2xl p-0"
        align={'start'}
      >
        <div className="p-2 border-b">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item..."
            autoFocus
          />
        </div>

        <div className="max-h-60 overflow-auto p-1">
          {filteredItems.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No item found.</div>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item._id}
                onClick={() => handleSelect(item)}
                className={cn(
                  'flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                  value === item._id && 'bg-accent'
                )}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4 shrink-0',
                    value === item._id ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <div className="flex flex-col">
                  <span className="font-medium">{item.itemName}</span>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                  <span className="text-xs text-primary mt-1">
                    ₹{item.sellingPrice || 0} •{' '}
                    {item.itemType === 'service' ? item.sacCode : item.hsnCode}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t p-1">
          <div
            onClick={handleAddNew}
            className="flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm text-primary hover:bg-accent"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New Item
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ItemDropdown;
